import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Plus } from 'lucide-react';

const SLOT_HEIGHT = 48;

export const StaffDayView = ({ selectedDate, services, center, staff, onNewBooking, onBookingClick }) => {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const res = await api.get(`/bookings?date=${dateStr}`);
        if (!cancelled) setBookings(res.data || []);
      } catch {
        if (!cancelled) setBookings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBookings();
    return () => { cancelled = true; };
  }, [api, selectedDate]); // eslint-disable-line

  const timeSlots = useMemo(() => {
    const [openHour, openMin] = (center?.openingTime || '08:00').split(':').map(Number);
    const [closeHour, closeMin] = (center?.closingTime || '21:00').split(':').map(Number);
    const slots = [];
    let h = openHour, m = openMin;
    while (h < closeHour || (h === closeHour && m < closeMin)) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      m += 30;
      if (m >= 60) { m -= 60; h++; }
    }
    return slots;
  }, [center]);

  const openingMinutes = useMemo(() => {
    const [h, m] = (center?.openingTime || '08:00').split(':').map(Number);
    return h * 60 + m;
  }, [center]);

  const serviceMap = useMemo(() => {
    const map = {};
    for (const s of services) map[s.id] = s;
    return map;
  }, [services]);

  const activeBookings = useMemo(
    () => bookings.filter(b => b.status !== 'CANCELLED'),
    [bookings]
  );

  const columns = useMemo(() => [
    ...staff.map(s => ({ id: s.id, label: s.fullName || s.name || s.id })),
    { id: '__unassigned', label: 'Sin asignar' },
  ], [staff]);

  const bookingsByStaff = useMemo(() => {
    const map = {};
    for (const col of columns) map[col.id] = [];
    for (const b of activeBookings) {
      const key = b.staffId && map[b.staffId] !== undefined ? b.staffId : '__unassigned';
      map[key].push(b);
    }
    return map;
  }, [activeBookings, columns]);

  // bookingId → { idx, count } — same logic as Planner.js startingBookings:
  // bookings that start at the same HH:mm within a column are split side-by-side.
  const bookingLayout = useMemo(() => {
    const layout = {};
    for (const col of columns) {
      const groups = {};
      for (const b of (bookingsByStaff[col.id] || [])) {
        const s = parseISO(b.startDateTime);
        const key = `${String(s.getHours()).padStart(2, '0')}:${String(s.getMinutes()).padStart(2, '0')}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(b);
      }
      for (const group of Object.values(groups)) {
        const count = group.length;
        group.forEach((b, idx) => {
          layout[b.id || b.bookingId] = { idx, count };
        });
      }
    }
    return layout;
  }, [columns, bookingsByStaff]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (activeBookings.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
        No hay citas para este día
      </div>
    );
  }

  const totalHeight = timeSlots.length * SLOT_HEIGHT;

  return (
    <div className="flex-1 overflow-auto planner-scroll">
      <div className="min-w-[600px]">

        {/* Header */}
        <div className="flex border-b border-zinc-800 sticky top-0 z-40 bg-background/95 backdrop-blur-md">
          <div className="w-16 flex-shrink-0 border-r border-zinc-800" />
          {columns.map(col => (
            <div
              key={col.id}
              className="flex-1 min-w-[140px] px-3 py-3 border-r border-zinc-800 last:border-r-0 text-center"
            >
              <div className="flex items-center justify-center gap-1">
                <span className={`text-xs font-semibold uppercase tracking-wide ${
                  col.id === '__unassigned' ? 'text-zinc-500' : 'text-zinc-300'
                }`}>
                  {col.label}
                </span>
                {col.id !== '__unassigned' && onNewBooking && (
                  <button
                    onClick={() => onNewBooking(col.id)}
                    className="p-0.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-zinc-200 transition-colors"
                    title="Nueva cita"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex">

          {/* Time axis */}
          <div className="w-16 flex-shrink-0 border-r border-zinc-800">
            {timeSlots.map(slot => (
              <div
                key={slot}
                className="px-2 pt-1 border-b border-zinc-800/40"
                style={{ height: SLOT_HEIGHT }}
              >
                <span className="text-[10px] text-zinc-600">{slot}</span>
              </div>
            ))}
          </div>

          {/* Staff columns */}
          {columns.map(col => (
            <div
              key={col.id}
              className="flex-1 min-w-[140px] relative border-r border-zinc-800 last:border-r-0"
              style={{ height: totalHeight }}
            >
              {/* Slot grid lines */}
              {timeSlots.map((_, i) => (
                <div
                  key={i}
                  className="absolute w-full border-b border-zinc-800/40 pointer-events-none"
                  style={{ top: i * SLOT_HEIGHT, height: SLOT_HEIGHT }}
                />
              ))}

              {/* Bookings */}
              {(bookingsByStaff[col.id] || []).map(booking => {
                const bId = booking.id || booking.bookingId;
                const start = parseISO(booking.startDateTime);
                const minutesFromOpen = (start.getHours() * 60 + start.getMinutes()) - openingMinutes;
                const topOffset = (minutesFromOpen / 30) * SLOT_HEIGHT;
                const blockHeight = Math.max(
                  ((booking.durationMinutes || 30) / 30) * SLOT_HEIGHT,
                  SLOT_HEIGHT
                );
                const svc = serviceMap[booking.calendarId];
                const { idx = 0, count = 1 } = bookingLayout[bId] || {};
                const gutter = 4;
                const widthPct = 100 / count;
                const leftPct = idx * widthPct;

                return (
                  <div
                    key={bId}
                    className="absolute rounded overflow-hidden px-2 py-1 text-[11px] cursor-pointer"
                    style={{
                      top: topOffset + 2,
                      height: blockHeight - 4,
                      left: `calc(${leftPct}% + ${gutter / 2}px)`,
                      width: `calc(${widthPct}% - ${gutter}px)`,
                      backgroundColor: svc?.color ? `${svc.color}22` : '#27272a',
                      borderLeft: `3px solid ${svc?.color || '#52525b'}`,
                    }}
                    title={`${booking.clientName || ''} — ${svc?.name || ''}`}
                    onClick={(e) => onBookingClick?.(booking, e)}
                  >
                    <div className="font-medium text-zinc-100 truncate leading-tight">
                      {booking.clientName || '—'}
                    </div>
                    <div className="text-zinc-400 truncate">{svc?.name || ''}</div>
                    <div className="text-zinc-500 flex items-center gap-1">
                      <span>{format(start, 'HH:mm')}</span>
                      {/* 4.2-fix (R1.3, R1.4, R5.1): distintivo visual —
                          activado SOLO por `staffAssignmentStatus ===
                          "PENDING"`, nunca por la mera ausencia de
                          `staffId`. No se toca la agrupación en la columna
                          `__unassigned` (línea ~69); una reserva legacy sin
                          staff cae ahí igual que antes pero sin este
                          distintivo.
                          Renderizado como marcador compacto en la misma fila
                          que la hora (no una 4ª línea completa) porque la
                          tarjeta es de altura fija con overflow-hidden y se
                          recorta al mínimo de 1 slot (48px, ~3 líneas de
                          texto ya ocupadas por cliente/servicio/hora). */}
                      {booking.staffAssignmentStatus === 'PENDING' && (
                        <span
                          className="inline-flex items-center gap-0.5 text-amber-400 font-medium shrink-0"
                          title="Pendiente de asignar personal"
                          data-testid={`staffdayview-pending-badge-${bId}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};
